import React from "react";
import axios from "axios";
import { formatInTimeZone } from "date-fns-tz";
import {
  MdArrowBack,
  MdCalculate,
  MdCheckCircle,
  MdLock,
  MdOutlineRefresh,
  MdWarningAmber,
} from "react-icons/md";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";
import "./lesson-confirmation-payroll.css";

export const PAYROLL_CALCULATION_CONFIRMATION =
  "CALCULATE_LESSON_CONFIRMATION_PAYROLL";

const currentPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const formatMoney = (cents) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((Number.isInteger(cents) ? cents : 0) / 100);

const formatOccurrenceMoney = (cents) =>
  Number.isInteger(cents) ? formatMoney(cents) : "Needs review";

const formatMinutes = (minutes) => {
  const safeMinutes = Number.isInteger(minutes) ? minutes : 0;
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  if (!hours) return `${remainder}m`;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
};

const formatPayrollTime = (minutes) => {
  const safeMinutes = Number.isInteger(minutes) ? minutes : 0;
  return `${formatMinutes(safeMinutes)} · ${safeMinutes.toLocaleString()} min`;
};

const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const reasonLabel = (reason) => {
  const labels = {
    student_cancelled_more_than_24_hours: "Student cancelled more than 24 hours ahead",
    student_cancelled_less_than_24_hours: "Student cancelled less than 24 hours ahead",
    teacher_cancelled: "Teacher cancelled",
  };
  return labels[reason] || titleCase(reason) || "—";
};

const answerLabel = (answer) => {
  if (answer === "taught") return "Taught";
  if (answer === "not_taught") return "Not taught";
  return titleCase(answer) || "Pending";
};

const formatDate = (value, timeZone, pattern = "MMM d, yyyy · h:mm a zzz") => {
  if (!value) return "—";
  try {
    return formatInTimeZone(new Date(value), timeZone || "UTC", pattern);
  } catch (_error) {
    return "—";
  }
};

const formatPeriod = (periodKey) => {
  if (!/^\d{4}-\d{2}$/.test(String(periodKey || ""))) return periodKey || "—";
  return new Date(`${periodKey}-01T12:00:00`).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
};

const normalizeError = (error, fallback) => ({
  status: error?.response?.status || null,
  code: error?.response?.data?.code || "",
  message: error?.response?.data?.error || error?.message || fallback,
});

const PayrollChip = ({ status }) => {
  const protectedStatus = ["approved", "locked", "paid"].includes(status);
  return (
    <span
      className={`lesson-payroll__chip lesson-payroll__chip--${
        status === "draft"
          ? "warning"
          : protectedStatus
          ? "protected"
          : "success"
      }`}
    >
      {protectedStatus && <MdLock aria-hidden="true" />}
      {titleCase(status)}
    </span>
  );
};

const PayrollError = ({ error, onRetry }) => {
  if (!error) return null;
  return (
    <div className="lesson-payroll__error" role="alert">
      <MdWarningAmber aria-hidden="true" />
      <div>
        <strong>
          {error.status === 403 ? "Administrator access required" : "Unable to load payroll"}
        </strong>
        {error.code && <code>{error.code}</code>}
        <p>{error.message}</p>
      </div>
      {onRetry && <button type="button" onClick={onRetry}>Try again</button>}
    </div>
  );
};

const PayrollSkeleton = () => (
  <div className="lesson-payroll__skeleton" aria-label="Loading monthly payroll">
    <div className="lesson-payroll__skeleton-cards">
      {[1, 2, 3, 4, 5].map((item) => <span key={item} />)}
    </div>
    {[1, 2, 3, 4].map((row) => (
      <div className="lesson-payroll__skeleton-row" key={row}>
        {[1, 2, 3, 4, 5].map((cell) => <span key={cell} />)}
      </div>
    ))}
  </div>
);

const PayrollSummary = ({ summary }) => (
  <section className="lesson-payroll__summary" aria-label="Monthly payroll totals">
    <div><span>Teachers</span><strong>{summary?.teacherCount || 0}</strong></div>
    <div><span>Billable time</span><strong>{formatPayrollTime(summary?.totalPayableMinutes)}</strong></div>
    <div><span>Base pay</span><strong>{formatMoney(summary?.basePayCents)}</strong></div>
    <div className="bonus"><span>Bonus pay</span><strong>{formatMoney(summary?.totalBonusCents)}</strong></div>
    <div className="total"><span>Total pay</span><strong>{formatMoney(summary?.grossPayCents)}</strong></div>
    <div className={(summary?.itemsNeedingReview || 0) > 0 ? "review" : ""}>
      <span>Needs review</span><strong>{summary?.itemsNeedingReview || 0}</strong>
    </div>
  </section>
);

const CalculationModal = ({ periodKey, busy, onCancel, onConfirm }) => (
  <div className="lesson-payroll__modal-backdrop" onMouseDown={busy ? undefined : onCancel}>
    <section
      className="lesson-payroll__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="calculate-payroll-title"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <header>
        <div className="lesson-payroll__modal-icon"><MdCalculate aria-hidden="true" /></div>
        <div>
          <h2 id="calculate-payroll-title">Calculate {formatPeriod(periodKey)} payroll?</h2>
          <p>The backend will recalculate draft and ready-for-review statements from submitted real lesson confirmations.</p>
        </div>
      </header>
      <div className="lesson-payroll__modal-body">
        <p><strong>Protected statements stay untouched.</strong> Approved, locked, and paid statements will be skipped.</p>
        <p>Demo lessons are excluded. Existing Timesheets and notifications will not be changed.</p>
      </div>
      <footer>
        <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="primary" onClick={onConfirm} disabled={busy}>
          {busy ? "Calculating…" : "Calculate monthly payroll"}
        </button>
      </footer>
    </section>
  </div>
);

const PayrollList = ({ dataMode, onChangeDataMode, onSelectStatement }) => {
  const [periodKey, setPeriodKey] = React.useState(currentPeriod);
  const [filters, setFilters] = React.useState({ teacherId: "", school: "", status: "" });
  const [cursor, setCursor] = React.useState("");
  const [cursorHistory, setCursorHistory] = React.useState([]);
  const [result, setResult] = React.useState({
    items: [], summary: {}, filterOptions: { teachers: [], schools: [], statuses: [] }, nextCursor: null,
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [showCalculation, setShowCalculation] = React.useState(false);
  const [calculating, setCalculating] = React.useState(false);
  const [calculationResult, setCalculationResult] = React.useState(null);
  const isDemoPreview = dataMode === "demo";

  const loadPayroll = React.useCallback(async (requestedCursor = cursor) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${baseUrl}/native/lesson-confirmations/admin/payroll${
          isDemoPreview ? "/demo-preview" : ""
        }`,
        {
          ...getRequestHeaders(),
          params: {
            period: periodKey,
            ...filters,
            limit: 50,
            ...(!isDemoPreview && requestedCursor
              ? { cursor: requestedCursor }
              : {}),
          },
        }
      );
      setResult({
        items: Array.isArray(response.data?.items) ? response.data.items : [],
        summary: response.data?.summary || {},
        filterOptions: response.data?.filterOptions || { teachers: [], schools: [], statuses: [] },
        nextCursor: response.data?.nextCursor || null,
      });
    } catch (requestError) {
      setError(normalizeError(requestError, "Unable to load monthly payroll statements."));
    } finally {
      setLoading(false);
    }
  }, [cursor, filters, isDemoPreview, periodKey]);

  React.useEffect(() => { loadPayroll(cursor); }, [cursor, loadPayroll]);

  const updateFilter = (key, value) => {
    setCursor("");
    setCursorHistory([]);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const changePeriod = (value) => {
    setPeriodKey(value);
    setCursor("");
    setCursorHistory([]);
    setCalculationResult(null);
  };

  const changeDataMode = (value) => {
    onChangeDataMode(value);
    setFilters({ teacherId: "", school: "", status: "" });
    setCursor("");
    setCursorHistory([]);
    setCalculationResult(null);
  };

  const calculate = async () => {
    setCalculating(true);
    setError(null);
    try {
      const response = await axios.post(
        `${baseUrl}/native/lesson-confirmations/admin/payroll/${encodeURIComponent(periodKey)}/calculate`,
        { confirmation: PAYROLL_CALCULATION_CONFIRMATION },
        getRequestHeaders()
      );
      setCalculationResult(response.data);
      setShowCalculation(false);
      setCursor("");
      setCursorHistory([]);
      await loadPayroll("");
    } catch (requestError) {
      setError(normalizeError(requestError, "Unable to calculate this payroll month."));
      setShowCalculation(false);
    } finally {
      setCalculating(false);
    }
  };

  const nextPage = () => {
    if (!result.nextCursor) return;
    setCursorHistory((history) => [...history, cursor]);
    setCursor(result.nextCursor);
  };

  const previousPage = () => {
    setCursorHistory((history) => {
      if (!history.length) return history;
      setCursor(history[history.length - 1] || "");
      return history.slice(0, -1);
    });
  };

  return (
    <>
      <header className="lesson-payroll__header">
        <div>
          <p className="lesson-payroll__eyebrow">{isDemoPreview ? "Safe testing mode" : "Lesson confirmation payroll"}</p>
          <h1>{isDemoPreview ? "Demo Payroll Preview" : "Monthly Payroll & Bonuses"}</h1>
          <p>{isDemoPreview ? "A non-persisted preview of submitted demo confirmations." : "Backend-calculated pay from submitted lesson confirmations."}</p>
        </div>
        <div className="lesson-payroll__header-actions">
          <button type="button" className="secondary" onClick={() => loadPayroll(cursor)} disabled={loading}>
            <MdOutlineRefresh aria-hidden="true" /> {loading ? "Refreshing…" : "Refresh"}
          </button>
          {!isDemoPreview && (
            <button type="button" className="primary" onClick={() => setShowCalculation(true)} disabled={loading}>
              <MdCalculate aria-hidden="true" /> Calculate / Refresh Month
            </button>
          )}
        </div>
      </header>

      <section className="lesson-payroll__filters" aria-label="Payroll filters">
        <label>
          Data
          <select aria-label="Payroll data" value={dataMode} onChange={(event) => changeDataMode(event.target.value)}>
            <option value="real">Real payroll</option>
            <option value="demo">Demo preview</option>
          </select>
        </label>
        <label>Month<input type="month" value={periodKey} onChange={(event) => changePeriod(event.target.value)} /></label>
        <label>
          School
          <select value={filters.school} onChange={(event) => updateFilter("school", event.target.value)}>
            <option value="">All schools</option>
            {(result.filterOptions?.schools || []).map((school) => <option key={school} value={school}>{school}</option>)}
          </select>
        </label>
        <label>
          Teacher
          <select value={filters.teacherId} onChange={(event) => updateFilter("teacherId", event.target.value)}>
            <option value="">All teachers</option>
            {(result.filterOptions?.teachers || []).map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.name}</option>)}
          </select>
        </label>
        <label>
          Statement status
          <select value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
            <option value="">All statuses</option>
            {(result.filterOptions?.statuses || []).map((status) => <option key={status} value={status}>{titleCase(status)}</option>)}
          </select>
        </label>
      </section>

      {isDemoPreview && (
        <div className="lesson-payroll__demo-notice" role="status">
          <MdWarningAmber aria-hidden="true" />
          <div>
            <strong>Demo preview only — excluded from payroll</strong>
            <span>These figures are calculated for testing, are not saved as pay statements, and cannot be approved, locked, or paid.</span>
          </div>
        </div>
      )}

      {calculationResult && (
        <div className="lesson-payroll__calculation-result" role="status">
          <MdCheckCircle aria-hidden="true" />
          <div>
            <strong>{calculationResult.calculatedCount || 0} statement(s) calculated</strong>
            <span>
              {calculationResult.sourceOccurrenceCount || 0} real submitted lessons · {calculationResult.skippedCount || 0} protected statement(s) skipped
            </span>
          </div>
        </div>
      )}
      <PayrollError error={error} onRetry={() => loadPayroll(cursor)} />
      {loading ? (
        <PayrollSkeleton />
      ) : !error ? (
        <>
          <PayrollSummary summary={result.summary} />
          {result.items.length === 0 ? (
            <div className="lesson-payroll__empty">
              <MdCalculate aria-hidden="true" />
              <h2>No {isDemoPreview ? "submitted demo confirmations" : "payroll statements"} for {formatPeriod(periodKey)}</h2>
              <p>{isDemoPreview ? "Submit a demo confirmation day to see it in this safe preview." : "Calculate the month after teachers have submitted their lesson confirmations."}</p>
            </div>
          ) : (
            <div className="lesson-payroll__table-card">
              <div className="lesson-payroll__table-scroll">
                <table className="lesson-payroll__table">
                  <thead><tr><th>Teacher</th><th>Billable</th><th>Lesson counts</th><th>Base pay</th><th>Bonus</th><th>Total</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
                  <tbody>
                    {result.items.map((statement) => (
                      <tr key={statement.id}>
                        <td><strong>{statement.teacher?.name || statement.teacher?.id}</strong><small>{statement.school || "No school"}</small></td>
                        <td><strong>{formatPayrollTime(statement.summary?.totalPayableMinutes)}</strong><small>{formatPayrollTime(statement.summary?.bonusEligibleMinutes)} bonus eligible</small></td>
                        <td>
                          <span>{statement.summary?.taughtLessonCount || 0} taught</span>
                          <small>{statement.summary?.paidCancellationCount || 0} paid · {statement.summary?.unpaidCancellationCount || 0} unpaid student · {statement.summary?.teacherCancellationCount || 0} teacher cancellations</small>
                          {(statement.summary?.unconfirmedOccurrenceCount || 0) > 0 && <small className="warning">{statement.summary.unconfirmedOccurrenceCount} need review</small>}
                        </td>
                        <td><strong>{formatMoney(statement.summary?.basePayCents)}</strong></td>
                        <td><strong className="lesson-payroll__bonus-pay">{formatMoney(statement.summary?.totalBonusCents)}</strong></td>
                        <td><strong>{formatMoney(statement.summary?.grossPayCents)}</strong></td>
                        <td><PayrollChip status={statement.status} />{statement.isDemo && <small className="lesson-payroll__demo-label">Demo only</small>}</td>
                        <td><button type="button" className="lesson-payroll__view" onClick={() => onSelectStatement(statement)} aria-label={`View ${statement.teacher?.name || statement.teacher?.id} payroll`}>View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <footer className="lesson-payroll__pagination">
                <span>{isDemoPreview ? "Previewed" : "Calculated"} {result.items[0]?.calculatedAt ? formatDate(result.items[0].calculatedAt, result.items[0].timeZone) : "—"}</span>
                {!isDemoPreview && <div><button type="button" onClick={previousPage} disabled={!cursorHistory.length}>Previous</button><button type="button" onClick={nextPage} disabled={!result.nextCursor}>Next</button></div>}
              </footer>
            </div>
          )}
        </>
      ) : null}
      {showCalculation && <CalculationModal periodKey={periodKey} busy={calculating} onCancel={() => setShowCalculation(false)} onConfirm={calculate} />}
    </>
  );
};

const DetailStat = ({ label, value, tone = "" }) => (
  <div className={tone}><span>{label}</span><strong>{value}</strong></div>
);

const PayrollDetail = ({ selection, onBack }) => {
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const loadDetail = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const detailPath = selection.isDemo
        ? `/native/lesson-confirmations/admin/payroll/demo-preview/${encodeURIComponent(selection.periodKey)}/${encodeURIComponent(selection.teacher?.id || "")}`
        : `/native/lesson-confirmations/admin/payroll/${encodeURIComponent(selection.id)}`;
      const response = await axios.get(
        `${baseUrl}${detailPath}`,
        getRequestHeaders()
      );
      setDetail(response.data);
    } catch (requestError) {
      setError(normalizeError(requestError, "Unable to load this pay statement."));
    } finally {
      setLoading(false);
    }
  }, [selection]);

  React.useEffect(() => { loadDetail(); }, [loadDetail]);

  if (loading && !detail) return <PayrollSkeleton />;
  const statement = detail?.statement;
  const summary = statement?.summary || {};
  return (
    <>
      <button type="button" className="lesson-payroll__back" onClick={onBack}><MdArrowBack aria-hidden="true" /> Back to monthly payroll</button>
      <PayrollError error={error} onRetry={loadDetail} />
      {statement && (
        <>
          <header className="lesson-payroll__detail-header">
            <div>
              <p className="lesson-payroll__eyebrow">{formatPeriod(statement.periodKey)}</p>
              <div><h1>{statement.teacher?.name || statement.teacher?.id}</h1><PayrollChip status={statement.status} /></div>
              <p>{statement.school || "No school"} · {statement.isDemo ? "Previewed" : "Calculated"} {formatDate(statement.calculatedAt, statement.timeZone)}</p>
            </div>
            <button type="button" className="secondary" onClick={loadDetail} disabled={loading}><MdOutlineRefresh aria-hidden="true" /> Refresh</button>
          </header>
          {statement.isDemo && (
            <div className="lesson-payroll__demo-notice" role="status">
              <MdWarningAmber aria-hidden="true" />
              <div><strong>Demo data — never included in real payroll</strong><span>This preview is not persisted and has no approval, lock, payment, Timesheet, or notification effect.</span></div>
            </div>
          )}
          <section className="lesson-payroll__detail-summary" aria-label="Teacher payroll summary">
            <DetailStat label="Billable time" value={formatPayrollTime(summary.totalPayableMinutes)} />
            <DetailStat label="Bonus eligible" value={formatPayrollTime(summary.bonusEligibleMinutes)} />
            <DetailStat label="Base pay" value={formatMoney(summary.basePayCents)} />
            <DetailStat label="Bonus pay" value={formatMoney(summary.totalBonusCents)} tone="bonus" />
            <DetailStat label="Adjustments" value={formatMoney(summary.adjustmentCents)} />
            <DetailStat label="Total pay" value={formatMoney(summary.grossPayCents)} tone="total" />
          </section>
          <section className="lesson-payroll__breakdown-grid">
            <article>
              <h2>Lesson breakdown</h2>
              <dl>
                <div><dt>Taught lessons</dt><dd>{summary.taughtLessonCount || 0}</dd></div>
                <div><dt>Paid cancellations</dt><dd>{summary.paidCancellationCount || 0}</dd></div>
                <div><dt>Unpaid student cancellations</dt><dd>{summary.unpaidCancellationCount || 0}</dd></div>
                <div><dt>Teacher cancellations</dt><dd>{summary.teacherCancellationCount || 0}</dd></div>
                <div><dt>Intro payable time</dt><dd>{formatMinutes(summary.introPayableMinutes)}</dd></div>
                <div><dt>Make-up payable time</dt><dd>{formatMinutes(summary.makeupPayableMinutes)}</dd></div>
                <div className={(summary.unconfirmedOccurrenceCount || 0) > 0 ? "warning" : ""}><dt>Items needing review</dt><dd>{summary.unconfirmedOccurrenceCount || 0}</dd></div>
              </dl>
            </article>
            <article>
              <h2>Bonus tiers</h2>
              <dl>
                <div><dt>First 15 eligible hours</dt><dd>No bonus</dd></div>
                <div><dt>Hours 15–25 · $8/hour</dt><dd>{formatMoney(summary.tierOneBonusCents)}</dd></div>
                <div><dt>Hours above 25 · $12/hour</dt><dd>{formatMoney(summary.tierTwoBonusCents)}</dd></div>
                <div className="bonus"><dt>Total bonus</dt><dd>{formatMoney(summary.totalBonusCents)}</dd></div>
              </dl>
            </article>
            <article>
              <h2>Rate snapshots</h2>
              {(statement.rateSnapshots || []).length ? (
                <dl>{statement.rateSnapshots.map((rate) => <div key={rate.id}><dt>{formatMoney(rate.rateCentsPerHour)}/hour</dt><dd>{rate.occurrenceCount} lessons · {formatMinutes(rate.payableMinutes)}</dd></div>)}</dl>
              ) : <p>No payable rate snapshots.</p>}
            </article>
          </section>

          <section className="lesson-payroll__lessons" aria-label="Contributing lessons">
            <div className="lesson-payroll__section-title"><div><h2>Contributing lessons</h2><p>{detail.items?.length || 0} submitted real lesson occurrence(s)</p></div></div>
            {(detail.items || []).map((item) => (
              <article className={`lesson-payroll__lesson ${item.teacherPayStatus === "needs_review" ? "needs-review" : ""}`} key={item.id}>
                <div className="lesson-payroll__lesson-name">
                  <strong>{item.studentName || "Unknown student"}</strong>
                  <span>{item.familyName || "Unknown family"}</span>
                </div>
                <div><span>Date and time</span><strong>{formatDate(item.scheduledStartAt, item.timeZone, "MMM d · h:mm a")}</strong><small>{item.durationMinutes} minutes · {titleCase(item.lessonType)}</small></div>
                <div><span>Confirmation</span><strong>{answerLabel(item.confirmationStatus)}</strong><small>{item.notTaughtReason ? reasonLabel(item.notTaughtReason) : "No cancellation"}</small></div>
                <div><span>Pay</span><strong>{formatOccurrenceMoney(item.basePayCents)}</strong><small>{formatMinutes(item.payableMinutes)} · {titleCase(item.teacherPayStatus)}</small></div>
                <div><span>Bonus</span><strong className={item.bonusEligible ? "bonus" : ""}>{item.bonusEligible ? "Eligible" : "Not eligible"}</strong><small>{formatOccurrenceMoney(item.baseRateCentsPerHour)}{Number.isInteger(item.baseRateCentsPerHour) ? "/hour" : ""}</small></div>
              </article>
            ))}
          </section>

          {(statement.adjustments || []).length > 0 && (
            <section className="lesson-payroll__audit"><h2>Adjustments</h2>{statement.adjustments.map((adjustment) => <div key={adjustment.id}><strong>{adjustment.description}</strong><span>{formatMoney(adjustment.amountCents)} · {formatDate(adjustment.createdAt, statement.timeZone)}</span></div>)}</section>
          )}
          <section className="lesson-payroll__audit"><h2>Statement history</h2>{(statement.auditTrail || []).map((entry) => <div key={entry.id}><strong>{titleCase(entry.action)}</strong><p>{entry.note}</p><span>{entry.performedById} · {formatDate(entry.performedAt, statement.timeZone)}</span></div>)}</section>
        </>
      )}
    </>
  );
};

const LessonConfirmationPayroll = ({ userData }) => {
  const [dataMode, setDataMode] = React.useState("real");
  const [selectedStatement, setSelectedStatement] = React.useState(null);
  if (!userData?.IsAdmin) return null;
  return selectedStatement ? (
    <PayrollDetail selection={selectedStatement} onBack={() => setSelectedStatement(null)} />
  ) : (
    <PayrollList
      dataMode={dataMode}
      onChangeDataMode={setDataMode}
      onSelectStatement={setSelectedStatement}
    />
  );
};

export default LessonConfirmationPayroll;
